const tryParseJson = (value) => {
  if (typeof value !== 'string') return null

  const trimmedValue = value.trim()
  if (!trimmedValue) return null
  if (!['{', '[', '"'].includes(trimmedValue[0])) return null

  try {
    return JSON.parse(trimmedValue)
  } catch (error) {
    return null
  }
}

const isNotificationRecord = (value) => {
  if (!value || Array.isArray(value) || typeof value !== 'object') return false

  return Boolean(
    value.notificationId ||
      value.notificationType ||
      value.createdTimeStamp ||
      value.updatedTimeStamp ||
      value.expiryDate ||
      value.subject,
  )
}

const parseMessageValue = (value) => {
  if (value == null) return null

  if (Array.isArray(value)) return value
  if (typeof value === 'object') return value

  const message = String(value).trim()
  if (!message) return null

  const parsed = tryParseJson(message)
  if (Array.isArray(parsed)) return parsed
  if (parsed && typeof parsed === 'object') return parsed
  if (parsed != null) return { description: String(parsed) }

  return { description: message }
}

export const parseNotificationMessage = (notification) => {
  if (typeof notification === 'string') return parseMessageValue(notification)
  if (!notification || typeof notification !== 'object') return null

  const rawMessage = notification.messages ?? notification.message ?? notification.body

  if (rawMessage == null) return notification

  return parseMessageValue(rawMessage)
}

export const normalizeNotificationText = (value) => {
  if (value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

export const isNotificationUnread = (notification) => {
  const status = normalizeNotificationText(notification?.status).toLowerCase()
  return !['read', 'seen', 'viewed'].includes(status)
}

export const getNotificationCategory = (value) => {
  const normalizedType = normalizeNotificationText(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

  if (normalizedType === 'generic') return 'market'
  if (normalizedType === 'personalized') return 'forYou'

  return 'forYou'
}

const parseNotificationTimestamp = (value) => {
  if (value == null) return null

  if (value instanceof Date) {
    const timestamp = value.getTime()
    return Number.isNaN(timestamp) ? null : timestamp
  }

  if (typeof value === 'number') {
    const timestamp = value < 1000000000000 ? value * 1000 : value
    return Number.isNaN(timestamp) ? null : timestamp
  }

  const timestampText = String(value).trim()
  if (!timestampText) return null

  const normalizedTimestamp = timestampText.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T')
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedTimestamp)
  const timestampWithTimezone = hasTimezone ? normalizedTimestamp : `${normalizedTimestamp}Z`
  const parsedTimestamp = Date.parse(timestampWithTimezone)

  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp
}

const formatRelativeUnit = (value, singular, plural = `${singular}s`) =>
  `${value} ${value === 1 ? singular : plural} ago`

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const ONE_WEEK_MS = 7 * ONE_DAY_MS

export const isNotificationNewerThanOneDay = (value, now = Date.now()) => {
  const timestamp = parseNotificationTimestamp(value)
  if (!timestamp) return false

  return now - timestamp < ONE_DAY_MS
}

export const isNotificationNewerThanOneWeek = (value, now = Date.now()) => {
  const timestamp = parseNotificationTimestamp(value)
  if (!timestamp) return false

  return now - timestamp < ONE_WEEK_MS
}

export const formatRelativeNotificationTime = (value, now = Date.now()) => {
  const timestamp = parseNotificationTimestamp(value)
  if (!timestamp) return ''

  const diffSeconds = Math.max(0, Math.floor((now - timestamp) / 1000))
  if (diffSeconds < 60) return 'Just now'

  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) return `${diffMinutes} min ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return formatRelativeUnit(diffHours, 'hour')

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return formatRelativeUnit(diffDays, 'day')

  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 5) return formatRelativeUnit(diffWeeks, 'week')

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) return formatRelativeUnit(Math.max(1, diffMonths), 'month')

  const diffYears = Math.floor(diffDays / 365)
  return formatRelativeUnit(Math.max(1, diffYears), 'year')
}

export const getNotificationList = (notifications) => {
  if (notifications == null) return []

  if (Array.isArray(notifications)) {
    return notifications.flatMap((notification) => getNotificationList(notification))
  }

  if (typeof notifications === 'string') {
    const parsedNotifications = tryParseJson(notifications)

    if (parsedNotifications != null) return getNotificationList(parsedNotifications)

    return [{ message: notifications }]
  }

  if (typeof notifications !== 'object') return [{ message: notifications }]

  if (isNotificationRecord(notifications)) return [notifications]

  if (notifications.response != null) return getNotificationList(notifications.response)
  if (notifications.data != null) return getNotificationList(notifications.data)
  if (notifications.notifications != null) return getNotificationList(notifications.notifications)

  const wrappedMessage = notifications.messages ?? notifications.message

  if (wrappedMessage != null) return getNotificationList(wrappedMessage)

  if (
    notifications.title ||
    notifications.description ||
    notifications.body ||
    notifications.route ||
    notifications.action
  ) {
    return [notifications]
  }

  return []
}

export const getParsedNotificationItems = (notifications, { onlyUnread = false } = {}) =>
  getNotificationList(notifications)
    .filter((notification) => !onlyUnread || isNotificationUnread(notification))
    .map((notification) => {
      const parsedMessage = parseNotificationMessage(notification)
      if (!parsedMessage) return null

      const title = normalizeNotificationText(parsedMessage.subject || notification?.subject)
      const description = normalizeNotificationText(
        parsedMessage.description || parsedMessage.message || parsedMessage.body,
      )
      const fallbackText = normalizeNotificationText(parsedMessage)
      const timestamp =
        notification?.createdTimeStamp ||
        notification?.createdTimestamp ||
        notification?.createdAt ||
        parsedMessage.createdTimeStamp ||
        parsedMessage.createdTimestamp ||
        parsedMessage.createdAt ||
        notification?.updatedTimeStamp ||
        notification?.updatedTimestamp ||
        notification?.updatedAt ||
        parsedMessage.updatedTimeStamp ||
        parsedMessage.updatedTimestamp ||
        parsedMessage.updatedAt
      const notificationType = normalizeNotificationText(
        notification?.notificationType || parsedMessage.notificationType || parsedMessage.type,
      )
      const text = [title, description || fallbackText].filter(Boolean).join(': ')

      if (!text) return null

      return {
        id: notification?.notificationId || `${title}-${description || fallbackText}`,
        notificationId: notification?.notificationId,
        title,
        description: description || fallbackText,
        text,
        action: normalizeNotificationText(parsedMessage.action),
        isUnread: isNotificationUnread(notification),
        route: normalizeNotificationText(parsedMessage.route),
        deeplink: normalizeNotificationText(
          parsedMessage.deeplink ||
            parsedMessage.deepLink ||
            notification?.deeplink ||
            notification?.deepLink,
        ),
        imageUrl: normalizeNotificationText(parsedMessage.imageUrl || notification?.imageUrl),
        timestamp,
        notificationType,
        notificationCategory: getNotificationCategory(notificationType),
      }
    })
    .filter(Boolean)
